import { useMemo } from 'react';
import type { Goal } from '@/types/goal';
import { Progress } from '@/components/ui/progress';
import { Target } from 'lucide-react';
import { format } from 'date-fns';

interface GoalMomentumProps {
  goals: Goal[];
  onNavigateToGoals: () => void;
}

export const GoalMomentum = ({ goals, onNavigateToGoals }: GoalMomentumProps) => {
  // Pick the top active goal (highest progress that isn't 100%)
  const topGoal = useMemo(() => {
    const active = goals.filter(g => g.progress < 100);
    if (active.length === 0) {
      return goals.length > 0 ? goals[0] : null;
    }
    return active.sort((a, b) => b.progress - a.progress)[0];
  }, [goals]);

  // Count weekly habit completions for this goal's habits
  const weeklyCompletions = useMemo(() => {
    if (!topGoal) return { thisWeek: 0, total: 0 };
    const today = new Date();
    let thisWeek = 0;
    let total = 0;

    for (const habit of topGoal.habits) {
      total += 7; // 7 days of potential completions
      for (let i = 0; i < 7; i++) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        const dateStr = format(date, 'yyyy-MM-dd');
        if (habit.completedDates.includes(dateStr)) {
          thisWeek++;
        }
      }
    }

    return { thisWeek, total };
  }, [topGoal]);

  if (!topGoal) return null;

  const activeCount = goals.filter(g => g.progress < 100).length;
  const completionRate = weeklyCompletions.total > 0
    ? Math.round((weeklyCompletions.thisWeek / weeklyCompletions.total) * 100)
    : 0;

  return (
    <button
      onClick={onNavigateToGoals}
      className="w-full text-left p-4 rounded-xl bg-muted/30 border border-border/30 hover:border-primary/30 transition-all opacity-80"
    >
      <div className="flex items-center gap-2 mb-3">
        <Target className="w-4 h-4 text-primary" />
        <span className="text-sm font-medium">Top Goal</span>
        <span className="text-xs text-muted-foreground ml-auto">{activeCount} active</span>
      </div>
      <p className="text-sm font-medium truncate mb-2">{topGoal.title}</p>
      <div className="flex items-center gap-2 mb-1">
        <Progress value={topGoal.progress} className="flex-1 h-2" />
        <span className="text-xs font-medium text-muted-foreground">{topGoal.progress}%</span>
      </div>
      {topGoal.habits.length > 0 && completionRate > 0 && (
        <p className="text-xs text-muted-foreground mt-2">
          {completionRate}% habit completion this week
        </p>
      )}
    </button>
  );
};
